import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Volume2, VolumeX } from "lucide-react";
import { listNotifications, markNotificationsRead } from "../lib/api";
import { useUser } from "../context/UserContext";
import { toast } from "sonner";

const POLL_MS = 12000;
const SOUND_PREF_KEY = "cc_notif_sound";

/**
 * Creates short branded "ping" sounds using WebAudio so we don't bundle mp3s.
 * - new (default): two ascending sine blips (Fiverr-style)
 * - success: bright C-major arpeggio (order approved / delivery received by buyer)
 * - alert: low triangle blip (revisions, refunds)
 * - message: single soft sine (chat)
 */
function playPing(kind = "new") {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 0.0001;
    master.connect(ctx.destination);

    const blip = ({ freq, start, dur = 0.18, peak = 0.18, type = "sine" }) => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      osc.connect(g); g.connect(master);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };

    if (kind === "success") {
      blip({ freq: 523.25, start: 0,    dur: 0.16, peak: 0.16 }); // C5
      blip({ freq: 659.25, start: 0.10, dur: 0.16, peak: 0.18 }); // E5
      blip({ freq: 783.99, start: 0.20, dur: 0.22, peak: 0.20 }); // G5
    } else if (kind === "alert") {
      blip({ freq: 320, start: 0,    dur: 0.20, peak: 0.18, type: "triangle" });
      blip({ freq: 220, start: 0.18, dur: 0.26, peak: 0.16, type: "triangle" });
    } else if (kind === "message") {
      blip({ freq: 660, start: 0, dur: 0.22, peak: 0.14 });
    } else {
      // new (default): Fiverr-ish two-tone ping
      blip({ freq: 880, start: 0,    dur: 0.18, peak: 0.18 });
      blip({ freq: 1320, start: 0.12, dur: 0.18, peak: 0.16 });
    }
    setTimeout(() => ctx.close(), 800);
  } catch (_) { /* silent */ }
}

const SOUND_BY_KIND = {
  order_created: "new",
  ltc_payment_confirmed: "new",
  order_delivered: "success",
  order_approved: "success",
  ltc_payout_sent: "success",
  order_revision: "alert",
  order_refunded: "alert",
  message_received: "message",
};

const KIND_EMOJI = {
  order_created: "🎬",
  order_delivered: "📦",
  order_approved: "✅",
  order_revision: "↺",
  order_refunded: "↩️",
  message_received: "💬",
  ltc_payment_confirmed: "⚡",
  ltc_payout_sent: "💸",
};

export default function NotificationsCenter() {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_PREF_KEY) !== "off"; } catch { return true; }
  });
  const lastSeenRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  // Poll
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await listNotifications();
        if (cancelled) return;
        const newOnes = r.items.filter((n) => !seenIdsRef.current.has(n.id));
        if (newOnes.length && lastSeenRef.current !== null) {
          // Show a toast + ping for each fresh one (using its kind-specific sound).
          newOnes.forEach((n) => {
            toast(n.title, { description: n.body, duration: 5000 });
          });
          if (soundOn) {
            // Play the most "important" sound among the batch (success > alert > new > message).
            const order = ["success", "alert", "new", "message"];
            const sound = order
              .find((s) => newOnes.some((n) => SOUND_BY_KIND[n.kind] === s)) || "new";
            playPing(sound);
          }
        }
        r.items.forEach((n) => seenIdsRef.current.add(n.id));
        lastSeenRef.current = Date.now();
        setItems(r.items);
        setUnread(r.unread || 0);
      } catch (_) { /* keep polling */ }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.id, soundOn]);

  // Close on outside click
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const onOpen = async () => {
    setOpen((v) => !v);
    if (!open && unread > 0) {
      try {
        await markNotificationsRead();
        setUnread(0);
        setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      } catch (_) {}
    }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem(SOUND_PREF_KEY, next ? "on" : "off"); } catch {}
    if (next) playPing("new");
  };

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="nav-notif-btn"
        onClick={onOpen}
        className="relative p-2 rounded-lg text-[#a0a0b0] hover:text-white hover:bg-[#0a0a14] transition-colors"
        aria-label={unread > 0 ? `${unread} unread notifications` : "Notifications"}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#ff4444] text-white text-[9px] font-mono flex items-center justify-center border border-[#06060c]">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-[360px] max-w-[90vw] cc-elevated rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-[#15152a] flex items-center justify-between">
            <div>
              <div className="cc-overline">Notifications</div>
              <div className="text-[11px] text-[#606070] mt-0.5">{items.length === 0 ? "No notifications yet" : `${items.length} recent`}</div>
            </div>
            <button
              type="button"
              data-testid="notif-sound-toggle"
              onClick={toggleSound}
              title={soundOn ? "Mute sound" : "Enable sound"}
              className={`p-1.5 rounded-md border transition ${soundOn ? "border-[#00ff88]/30 text-[#00ff88]" : "border-[#15152a] text-[#606070]"}`}
            >
              {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto divide-y divide-[#15152a]">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-[#606070] text-sm">
                You&apos;ll see new orders, deliveries, payments and messages here.
              </div>
            )}
            {items.map((n) => (
              <NotifRow key={n.id} n={n} onClose={() => setOpen(false)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ n, onClose }) {
  const ago = useMemo(() => relativeTime(n.created_at), [n.created_at]);
  const body = (
    <div className={`flex items-start gap-3 px-4 py-3 ${!n.read ? "bg-[#00ff88]/[0.04]" : ""} hover:bg-[#0a0a14] transition-colors`}>
      <span className="text-base shrink-0 mt-0.5">{KIND_EMOJI[n.kind] || "🔔"}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">{n.title}</div>
        <div className="text-[11px] text-[#a0a0b0] line-clamp-2 mt-0.5 leading-relaxed">{n.body}</div>
        <div className="text-[10px] text-[#606070] uppercase tracking-widest mt-1">{ago}</div>
      </div>
      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] mt-1.5 shrink-0" />}
    </div>
  );
  return n.order_id ? (
    <Link to={`/orders/${n.order_id}`} onClick={onClose} data-testid={`notif-${n.id}`}>{body}</Link>
  ) : (
    <div data-testid={`notif-${n.id}`}>{body}</div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
